import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import { tpaColorName } from '../../../../../utils/color';

export type DesignStylesParams = {
  titleColor: StyleParamType.Color;
  titleFont: StyleParamType.Font;
  statusColor: StyleParamType.Color;
  statusFont: StyleParamType.Font;
  subtitleColor: StyleParamType.Color;
  subtitleFont: StyleParamType.Font;
  sectionLabelColor: StyleParamType.Color;
  sectionLabelFont: StyleParamType.Font;
  bodyColor: StyleParamType.Color;
  bodyFont: StyleParamType.Font;

  backgroundColor: StyleParamType.Color;
  backgroundBorderWidth: StyleParamType.Number;
  backgroundBorderColor: StyleParamType.Color;
  backgroundCornerRadiusTopLeft: StyleParamType.Number;
  backgroundCornerRadiusTopRight: StyleParamType.Number;
  backgroundCornerRadiusBottomLeft: StyleParamType.Number;
  backgroundCornerRadiusBottomRight: StyleParamType.Number;
  backgroundCornerRadiusLinked: StyleParamType.Number;
  dividerWidth: StyleParamType.Number;
  dividerColor: StyleParamType.Color;
};

export const designStylesParams = createStylesParams<DesignStylesParams>({
  titleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-heading-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-title', opacity: 1 },
      ai: {
        description:
          'Text color of the main title on the order tracking page shown after checkout.',
      },
    },
  },
  titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', { htmlTag: 'h1', size: 28 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Heading-M', size: 28 },
      ai: {
        description: 'Font of the main title on the order tracking page.',
      },
    },
  },
  statusColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: {
        description:
          'Text color of the order status text (e.g., received, preparing, out for delivery) on the order tracking page.',
      },
    },
  },
  statusFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: {
        description:
          'Font of the order status text on the order tracking page.',
      },
    },
  },
  subtitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-heading-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-title', opacity: 1 },
      ai: {
        description:
          'Text color of the subtitle headings on the order tracking page.',
      },
    },
  },
  subtitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', { htmlTag: 'h2', size: 24 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Heading-M', size: 24 },
      ai: {
        description:
          'Font of the subtitle headings on the order tracking page.',
      },
    },
  },
  sectionLabelColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: {
        description:
          'Text color of the section labels on the order tracking page.',
      },
    },
  },
  sectionLabelFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'h3', size: 18 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 18 },
      ai: {
        description: 'Font of the section labels on the order tracking page.',
      },
    },
  },
  bodyColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: {
        description:
          'Text color of the body text (order details) on the order tracking page.',
      },
    },
  },
  bodyFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { htmlTag: 'p', size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: {
        description:
          'Font of the body text (order details) on the order tracking page.',
      },
    },
  },
  backgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(
      tpaColorName('wst-primary-background-color'),
      1,
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: {
        description:
          'Background fill color of the order tracking page container.',
      },
    },
  },
  backgroundBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Border thickness in pixels of the order tracking page container.',
      },
    },
  },
  backgroundBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 0.12),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 0.12,
      },
      ai: {
        description: 'Border color of the order tracking page container.',
      },
    },
  },
  backgroundCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-left corner radius in pixels of the order tracking page container.',
      },
    },
  },
  backgroundCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-right corner radius in pixels of the order tracking page container.',
      },
    },
  },
  backgroundCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius in pixels of the order tracking page container.',
      },
    },
  },
  backgroundCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius in pixels of the order tracking page container.',
      },
    },
  },
  backgroundCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Controls whether the four corner radii of the order tracking page container are edited together: 1 = all corners locked together, 0 = each independent.',
      },
    },
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          'Thickness in pixels of the divider lines separating sections on the order tracking page.',
      },
    },
  },
  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(tpaColorName('wst-paragraph-2-color'), 0.12),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 0.12,
      },
      ai: {
        description:
          'Color of the divider lines separating sections on the order tracking page.',
      },
    },
  },
});
